import { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, Clock, Globe, Monitor, ChevronDown, ChevronUp, CheckCircle, Trash2, MessageSquare, X, Sparkles, Loader2, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import ReactMarkdown from 'react-markdown';

interface ClientError {
  id: string;
  fingerprint: string | null;
  error_type: string;
  message: string;
  stack: string | null;
  component_stack: string | null;
  url: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown>;
  status: string;
  admin_notes: string | null;
  created_at: string;
  resolved_at: string | null;
}

type StatusFilter = 'all' | 'open' | 'resolved' | 'ignored';

const ErrorTrackingPanel = () => {
  const [errors, setErrors] = useState<ClientError[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedError, setExpandedError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [notesValue, setNotesValue] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Record<string, string>>({});
  const [copiedSuggestion, setCopiedSuggestion] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchErrors = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('client_errors')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setErrors((data || []) as ClientError[]);
    } catch (err) {
      console.error('Failed to fetch errors:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchErrors();
  }, [statusFilter]);

  const formatTimestamp = (timestamp: string) => {
    const estTime = toZonedTime(new Date(timestamp), 'America/New_York');
    return format(estTime, 'MMM d, h:mm a');
  };

  const getErrorTypeBadge = (errorType: string) => {
    const colors: Record<string, string> = {
      RUNTIME_ERROR: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      REACT_ERROR_BOUNDARY: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      UNHANDLED_PROMISE_REJECTION: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      MANUAL_REPORT: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    };
    return colors[errorType] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'ignored':
        return 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
      default:
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    }
  };

  const parseUserAgent = (ua: string | null) => {
    if (!ua) return 'Unknown';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Other';
  };

  const handleUpdateStatus = async (id: string, newStatus: 'resolved' | 'ignored' | 'open') => {
    setProcessingId(id);
    try {
      const updateData: Record<string, unknown> = {
        status: newStatus,
      };

      if (newStatus === 'resolved') {
        updateData.resolved_at = new Date().toISOString();
      } else if (newStatus === 'open') {
        updateData.resolved_at = null;
      }

      const { error } = await supabase
        .from('client_errors')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      setErrors(prev => prev.map(e => 
        e.id === id 
          ? { ...e, status: newStatus, resolved_at: newStatus === 'resolved' ? new Date().toISOString() : null } 
          : e
      ));

      toast({
        title: 'Status updated',
        description: `Error marked as ${newStatus}`,
      });
    } catch (err) {
      console.error('Failed to update status:', err);
      toast({
        title: 'Failed to update',
        description: 'Could not update error status',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleSaveNotes = async (id: string) => {
    setProcessingId(id);
    try {
      const { error } = await supabase
        .from('client_errors')
        .update({ admin_notes: notesValue })
        .eq('id', id);

      if (error) throw error;

      setErrors(prev => prev.map(e => 
        e.id === id ? { ...e, admin_notes: notesValue } : e
      ));
      setEditingNotes(null);
      setNotesValue('');

      toast({
        title: 'Notes saved',
        description: 'Admin notes have been updated',
      });
    } catch (err) {
      console.error('Failed to save notes:', err);
      toast({
        title: 'Failed to save',
        description: 'Could not save notes',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this error? This cannot be undone.')) return;

    setProcessingId(id);
    try {
      const { error } = await supabase
        .from('client_errors')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setErrors(prev => prev.filter(e => e.id !== id));
      toast({
        title: 'Error deleted',
        description: 'The error has been removed',
      });
    } catch (err) {
      console.error('Failed to delete:', err);
      toast({
        title: 'Failed to delete',
        description: 'Could not delete error',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleAnalyzeError = async (error: ClientError) => {
    setAnalyzingId(error.id);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('analyze-error', {
        body: {
          errorType: error.error_type,
          message: error.message,
          stack: error.stack,
          componentStack: error.component_stack,
          url: error.url,
          metadata: error.metadata,
        },
      });

      if (fnError) throw fnError;

      if (data?.suggestion) {
        setSuggestions(prev => ({ ...prev, [error.id]: data.suggestion }));
        toast({
          title: 'Analysis complete',
          description: 'AI has suggested a fix for this error',
        });
      }
    } catch (err) {
      console.error('Failed to analyze:', err);
      toast({
        title: 'Analysis failed',
        description: 'Could not analyze the error',
        variant: 'destructive',
      });
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleCopySuggestion = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSuggestion(id);
    setTimeout(() => setCopiedSuggestion(null), 2000);
  };

  const openErrorCount = errors.filter(e => e.status === 'open').length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-destructive" />
          <h2 className="text-lg font-semibold">Error Tracking</h2>
          {openErrorCount > 0 && (
            <Badge variant="destructive">{openErrorCount} open</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden">
            {(['all', 'open', 'resolved', 'ignored'] as StatusFilter[]).map((filter) => (
              <button
                key={filter}
                onClick={() => setStatusFilter(filter)}
                className={`px-3 py-1.5 text-sm capitalize transition-colors ${
                  statusFilter === filter
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={fetchErrors}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {errors.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No {statusFilter !== 'all' ? statusFilter : ''} errors found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {errors.map((error) => (
            <div
              key={error.id}
              className={`border rounded-lg overflow-hidden ${
                error.status === 'resolved' 
                  ? 'border-green-200 dark:border-green-900/50' 
                  : error.status === 'ignored'
                    ? 'border-border opacity-60'
                    : 'border-border'
              }`}
            >
              <button
                onClick={() => setExpandedError(expandedError === error.id ? null : error.id)}
                className="w-full px-4 py-3 flex items-start gap-3 hover:bg-muted/50 transition-colors text-left"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(error.status)}`}>
                      {error.status}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getErrorTypeBadge(error.error_type)}`}>
                      {error.error_type.replace(/_/g, ' ')}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {formatTimestamp(error.created_at)}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Monitor className="w-3 h-3" />
                      {parseUserAgent(error.user_agent)}
                    </span>
                    {error.admin_notes && (
                      <span className="flex items-center gap-1 text-xs text-primary">
                        <MessageSquare className="w-3 h-3" />
                        Has notes
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-foreground truncate">
                    {error.message}
                  </p>
                  {error.url && (
                    <p className="flex items-center gap-1 text-xs text-muted-foreground mt-1 truncate">
                      <Globe className="w-3 h-3 flex-shrink-0" />
                      {new URL(error.url).pathname}
                    </p>
                  )}
                </div>
                {expandedError === error.id ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                )}
              </button>

              {expandedError === error.id && (
                <div className="px-4 pb-4 space-y-3 border-t border-border bg-muted/30">
                  {/* Action Buttons */}
                  <div className="pt-3 flex items-center gap-2 flex-wrap">
                    {error.status !== 'resolved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateStatus(error.id, 'resolved')}
                        disabled={processingId === error.id}
                        className="gap-1 text-green-600 border-green-200 hover:bg-green-50 dark:text-green-400 dark:border-green-800 dark:hover:bg-green-900/20"
                      >
                        <CheckCircle className="w-3 h-3" />
                        Mark Resolved
                      </Button>
                    )}
                    {error.status === 'resolved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateStatus(error.id, 'open')}
                        disabled={processingId === error.id}
                      >
                        Reopen
                      </Button>
                    )}
                    {error.status !== 'ignored' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateStatus(error.id, 'ignored')}
                        disabled={processingId === error.id}
                        className="text-muted-foreground"
                      >
                        Ignore
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingNotes(error.id);
                        setNotesValue(error.admin_notes || '');
                      }}
                      disabled={processingId === error.id}
                      className="gap-1"
                    >
                      <MessageSquare className="w-3 h-3" />
                      {error.admin_notes ? 'Edit Notes' : 'Add Notes'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(error.id)}
                      disabled={processingId === error.id}
                      className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleAnalyzeError(error)}
                      disabled={analyzingId === error.id}
                      className="gap-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                    >
                      {analyzingId === error.id ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3" />
                          Suggest Fix
                        </>
                      )}
                    </Button>
                  </div>

                  {/* AI Suggestion Display */}
                  {suggestions[error.id] && (
                    <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          <p className="text-sm font-medium text-purple-700 dark:text-purple-300">AI Fix Suggestion</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopySuggestion(error.id, suggestions[error.id])}
                          className="h-7 px-2"
                        >
                          {copiedSuggestion === error.id ? (
                            <Check className="w-3 h-3 text-green-600" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                      <div className="prose prose-sm dark:prose-invert max-w-none text-foreground [&>h1]:text-base [&>h2]:text-sm [&>h3]:text-sm [&>p]:text-sm [&>ul]:text-sm [&>ol]:text-sm [&>pre]:text-xs [&>pre]:bg-white/50 [&>pre]:dark:bg-black/20">
                        <ReactMarkdown>{suggestions[error.id]}</ReactMarkdown>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingNotes(error.id);
                            setNotesValue((error.admin_notes ? error.admin_notes + '\n\n---\n\n' : '') + 'AI Suggestion:\n' + suggestions[error.id]);
                          }}
                          className="text-xs"
                        >
                          Save to Notes
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSuggestions(prev => {
                            const next = { ...prev };
                            delete next[error.id];
                            return next;
                          })}
                          className="text-xs text-muted-foreground"
                        >
                          Dismiss
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Notes Editor */}
                  {editingNotes === error.id && (
                    <div className="space-y-2 p-3 bg-background rounded-lg border border-border">
                      <Textarea
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.target.value)}
                        placeholder="Add notes about this error, fix applied, etc."
                        className="min-h-[80px]"
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleSaveNotes(error.id)}
                          disabled={processingId === error.id}
                        >
                          Save Notes
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingNotes(null);
                            setNotesValue('');
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Existing Notes Display */}
                  {error.admin_notes && editingNotes !== error.id && (
                    <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                      <p className="text-xs font-medium text-primary mb-1">Admin Notes</p>
                      <p className="text-sm text-foreground whitespace-pre-wrap">{error.admin_notes}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Full Message</p>
                    <p className="text-sm text-foreground">{error.message}</p>
                  </div>

                  {error.stack && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Stack Trace</p>
                      <pre className="text-xs bg-muted p-3 rounded overflow-x-auto max-h-48 font-mono">
                        {error.stack}
                      </pre>
                    </div>
                  )}

                  {error.component_stack && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Component Stack</p>
                      <pre className="text-xs bg-muted p-3 rounded overflow-x-auto max-h-32 font-mono">
                        {error.component_stack}
                      </pre>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <p className="font-medium text-muted-foreground mb-1">Fingerprint</p>
                      <p className="font-mono">{error.fingerprint || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground mb-1">URL</p>
                      <p className="truncate">{error.url || 'N/A'}</p>
                    </div>
                    {error.resolved_at && (
                      <div className="col-span-2">
                        <p className="font-medium text-muted-foreground mb-1">Resolved At</p>
                        <p>{formatTimestamp(error.resolved_at)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ErrorTrackingPanel;
